import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { Chart } from "react-chartjs-2";
import { BarController, BarElement, CategoryScale, Chart as ChartJs, LinearScale, Tooltip } from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Alert, Button, ButtonGroup, Card, Grid, Icon, InputDateRange, InputSelect, Modal, Table, Text } from "~/components-shared";
import { CostsSortByEnum, DataUsageTypeEnum, StatusStateEnum } from "~/types/enums";
import costCharts from "~/charts/costs";
import costsActionCreators from "~/store/costs/actions";
import costSelectors from "~/store/costs/selectors";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import Markdown from "~/components/Markdown";
import { helpers } from "~/lib/helpers";
import lists from "~/lib/lists";
import moment from "moment";
import { useQueryState } from "~/hooks/common";
import CostExplorerFilterModal from "~/components/CostExplorerFilterModal";
import styled, { useTheme } from "styled-components";
import authSelectors from "~/store/auth/selectors";
ChartJs.register(BarController, CategoryScale, LinearScale, BarElement, Tooltip);
const StyledGrid = styled(Grid)`
  position: sticky;
  top: 0;
  z-index: 2;
    background-color ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.secondary;
}};
`;
const CostExplorerCard = _ref2 => {
  let {
    ...rest
  } = _ref2;
  const theme = useTheme();
  const {
    authorities
  } = useSelector(authSelectors.getUser);
  const {
    t
  } = useTranslation();
  const costs = useSelector(costSelectors.getCosts);
  const [queryParams, setQueryParams] = useQueryState({
    view: "totalTokenCount"
  });
  const [showFilter, setShowFilter] = useState(false);
  const status = useSelector(costSelectors.getStatus);
  const [sort, setSort] = useState(CostsSortByEnum.BY_DATE_DESC);
  const error = useSelector(costSelectors.getError);
  const dispatch = useDispatch();
  const columnCount = 6;
  const groupedCosts = helpers.groupBy(costs, "name");
  const startDate = useSelector(costSelectors.getFrom);
  const endDate = useSelector(costSelectors.getTo);
  const aggregatedBy = useSelector(costSelectors.getAggregatedBy);
  const handleAggregation = aggregateBy => {
    dispatch(costsActionCreators.aggregate(aggregateBy));
  };
  const handleDateSelect = (start, end) => {
    if (start) {
      dispatch(costsActionCreators.setFrom(start));
    }
    if (end) {
      dispatch(costsActionCreators.setTo(end));
    }
  };
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(costsActionCreators.fetchStarted());
    }
  }, [status]);
  const handleSort = newSort => {
    setSort(newSort);
  };
  const sortedCosts = Object.values(groupedCosts).sort((a, b) => {
    switch (sort) {
      case CostsSortByEnum.BY_DATE_DESC:
        return moment(b[0].date).valueOf() - moment(a[0].date).valueOf();
      case CostsSortByEnum.BY_DATE_ASC:
        return moment(a[0].date).valueOf() - moment(b[0].date).valueOf();
      case CostsSortByEnum.BY_COSTS_DESC:
        return b.reduce((prev, curr) => prev + curr.costs, 0) - a.reduce((prev, curr) => prev + curr.costs, 0);
      case CostsSortByEnum.BY_COSTS_ASC:
        return a.reduce((prev, curr) => prev + curr.costs, 0) - b.reduce((prev, curr) => prev + curr.costs, 0);
      case CostsSortByEnum.BY_REQUEST_COUNT_DESC:
        return b.reduce((prev, curr) => prev + curr.requestCount, 0) - a.reduce((prev, curr) => prev + curr.requestCount, 0);
      case CostsSortByEnum.BY_REQUEST_COUNT_ASC:
        return a.reduce((prev, curr) => prev + curr.requestCount, 0) - b.reduce((prev, curr) => prev + curr.requestCount, 0);
      case CostsSortByEnum.BY_TOTAL_TOKENS_DESC:
        return b.reduce((prev, curr) => prev + curr.totalTokenCount, 0) - a.reduce((prev, curr) => prev + curr.totalTokenCount, 0);
      case CostsSortByEnum.BY_TOTAL_TOKENS_ASC:
        return a.reduce((prev, curr) => prev + curr.totalTokenCount, 0) - b.reduce((prev, curr) => prev + curr.totalTokenCount, 0);
      case CostsSortByEnum.BY_INPUT_TOKENS_DESC:
        return b.reduce((prev, curr) => prev + curr.inputTokenCount, 0) - a.reduce((prev, curr) => prev + curr.inputTokenCount, 0);
      case CostsSortByEnum.BY_INPUT_TOKENS_ASC:
        return a.reduce((prev, curr) => prev + curr.inputTokenCount, 0) - b.reduce((prev, curr) => prev + curr.inputTokenCount, 0);
      case CostsSortByEnum.BY_OUTPUT_TOKENS_DESC:
        return b.reduce((prev, curr) => prev + curr.outputTokenCount, 0) - a.reduce((prev, curr) => prev + curr.outputTokenCount, 0);
      case CostsSortByEnum.BY_OUTPUT_TOKENS_ASC:
        return a.reduce((prev, curr) => prev + curr.outputTokenCount, 0) - b.reduce((prev, curr) => prev + curr.outputTokenCount, 0);
      case CostsSortByEnum.BY_VOLUME_DESC:
        return b.reduce((prev, curr) => prev + curr.volume, 0) - a.reduce((prev, curr) => prev + curr.volume, 0);
      case CostsSortByEnum.BY_VOLUME_ASC:
        return a.reduce((prev, curr) => prev + curr.volume, 0) - b.reduce((prev, curr) => prev + curr.volume, 0);
      default:
        return 1;
    }
  });
  const views = [{
    id: "totalTokenCount",
    title: t("CostExplorerCard.tokens"),
    onClick: id => setQueryParams({
      view: id
    })
  }, {
    id: "requestCount",
    title: t("CostExplorerCard.requestCount"),
    onClick: id => setQueryParams({
      view: id
    })
  }, {
    id: "volume",
    title: t("CostExplorerCard.volume"),
    onClick: id => setQueryParams({
      view: id
    })
  }, {
    id: "costs",
    title: t("CostExplorerCard.costs"),
    onClick: id => setQueryParams({
      view: id
    })
  }];
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 0
    },
    loading: status === StatusStateEnum.LOADING
  }, rest), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 6
    },
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: error || "",
    linkColor: "text"
  })), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 3,
    gap: 5,
    template: "22rem 10rem 1fr auto",
    s: {
      px: 5,
      py: 3,
      mx: -5,
      mt: -3,
      mb: 2
    }
  }, /*#__PURE__*/React.createElement(InputDateRange, {
    initialStartDate: startDate,
    initialEndDate: endDate,
    onChange: handleDateSelect
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: aggregatedBy,
    onChange: val => handleAggregation(val),
    name: "aggregatedBy",
    options: lists.costs.aggregatedBy.names
  }), /*#__PURE__*/React.createElement("div", null), /*#__PURE__*/React.createElement(Button, {
    s: {
      py: 2.8,
      px: 4
    },
    color: showFilter ? "translucentPrimary" : "translucent",
    onClick: () => setShowFilter(prev => !prev),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "tune",
      scale: 5,
      s: {
        mr: 1
      }
    })
  }, t("CostExplorerCard.filter"))), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 5,
    s: {
      mb: 5
    },
    template: "2fr 1fr 1fr"
  }, /*#__PURE__*/React.createElement(Card, {
    color: "transparent",
    border: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    equal: true
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 4,
    color: "textSecondary",
    s: {
      mb: 3
    }
  }, t("CostExplorerCard.totalTokens")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 6
  }, helpers.formatTokens(costs.reduce((prev, curr) => prev + curr.totalTokenCount, 0)))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 4,
    color: "textSecondary",
    s: {
      mb: 3
    }
  }, t("CostExplorerCard.input")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 6
  }, helpers.formatTokens(costs.reduce((prev, curr) => prev + curr.inputTokenCount, 0)))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 4,
    color: "textSecondary",
    s: {
      mb: 3
    }
  }, t("CostExplorerCard.output")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 6
  }, helpers.formatTokens(costs.reduce((prev, curr) => prev + curr.outputTokenCount, 0)))))), /*#__PURE__*/React.createElement(Card, {
    color: "transparent",
    border: true
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 4,
    color: "textSecondary",
    s: {
      mb: 3
    }
  }, t("CostExplorerCard.totalVolume")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 6
  }, helpers.formatVolumeToGB(costs.reduce((prev, curr) => prev + curr.volume, 0)))), /*#__PURE__*/React.createElement(Card, {
    color: "transparent",
    border: true
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 4,
    color: "textSecondary",
    s: {
      mb: 3
    }
  }, t("CostExplorerCard.totalCosts")), /*#__PURE__*/React.createElement(Text, {
    display: "inline-block",
    weight: "bold",
    fontSize: 6
  }, helpers.currency(costs.reduce((prev, curr) => prev + curr.costs, 0))))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(Card, {
    s: {
      mb: 5
    },
    title: /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      template: "1fr auto"
    }, /*#__PURE__*/React.createElement(Text, {
      element: "h3"
    }, t("CostExplorerCard.totalUsage")), /*#__PURE__*/React.createElement(ButtonGroup, {
      selected: queryParams.view,
      options: views
    })),
    color: "background",
    align: "left",
    shadow: true
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: 320,
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Chart, costCharts.explorer(theme, costs, queryParams.view))))), /*#__PURE__*/React.createElement(Grid, {
    s: {
      mb: 5
    },
    cols: 2,
    gap: 5,
    equal: true
  }, Object.keys(DataUsageTypeEnum).filter(item => authorities.includes(lists.costs.types.toAuthority[item])).map(key => /*#__PURE__*/React.createElement(ErrorBoundaryCard, {
    key: key
  }, /*#__PURE__*/React.createElement(Card, {
    title: /*#__PURE__*/React.createElement(Grid, {
      cols: 2,
      template: "1fr auto"
    }, /*#__PURE__*/React.createElement(Text, {
      element: "h3"
    }, lists.costs.types.names[key])),
    color: "background",
    align: "left",
    shadow: true
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      height: 240,
      width: "100%"
    }
  }, /*#__PURE__*/React.createElement(Chart, costCharts.explorer(theme, costs.filter(cost => cost.type === key || !cost.type), queryParams.view, true))))))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(Card, {
    color: "background",
    shadow: true
  }, /*#__PURE__*/React.createElement(Table, {
    size: "m",
    fontSize: 3.2,
    tableProps: {
      style: {
        minWidth: "650px"
      }
    }
  }, /*#__PURE__*/React.createElement(Table.Header, null, /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [CostsSortByEnum.BY_DATE_DESC, CostsSortByEnum.BY_DATE_ASC],
    onSort: handleSort
  }, t("CostExplorerCard.date")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "120px",
    sort: sort,
    sortBy: [CostsSortByEnum.BY_REQUEST_COUNT_DESC, CostsSortByEnum.BY_REQUEST_COUNT_ASC],
    onSort: handleSort,
    style: {
      textAlign: "right"
    }
  }, t("CostExplorerCard.requestCount")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px",
    sort: sort,
    sortBy: [CostsSortByEnum.BY_TOTAL_TOKENS_DESC, CostsSortByEnum.BY_TOTAL_TOKENS_ASC],
    onSort: handleSort,
    style: {
      textAlign: "right"
    }
  }, t("CostExplorerCard.totalTokens")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px",
    sort: sort,
    sortBy: [CostsSortByEnum.BY_INPUT_TOKENS_DESC, CostsSortByEnum.BY_INPUT_TOKENS_ASC],
    onSort: handleSort,
    style: {
      textAlign: "right"
    }
  }, t("CostExplorerCard.inputTokens")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px",
    sort: sort,
    sortBy: [CostsSortByEnum.BY_OUTPUT_TOKENS_DESC, CostsSortByEnum.BY_OUTPUT_TOKENS_ASC],
    onSort: handleSort,
    style: {
      textAlign: "right"
    }
  }, t("CostExplorerCard.outputTokens")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "100px",
    sort: sort,
    sortBy: [CostsSortByEnum.BY_VOLUME_DESC, CostsSortByEnum.BY_VOLUME_ASC],
    onSort: handleSort,
    style: {
      textAlign: "right"
    }
  }, t("CostExplorerCard.volume")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "70px",
    sort: sort,
    sortBy: [CostsSortByEnum.BY_COSTS_DESC, CostsSortByEnum.BY_COSTS_ASC],
    onSort: handleSort,
    style: {
      textAlign: "right"
    }
  }, t("CostExplorerCard.costs")))), /*#__PURE__*/React.createElement(Table.Body, null, sortedCosts.length ? /*#__PURE__*/React.createElement(React.Fragment, null, sortedCosts.map((cost, index) => /*#__PURE__*/React.createElement(Table.Tr, {
    key: index
  }, /*#__PURE__*/React.createElement(Table.Td, {
    style: {
      whiteSpace: "nowrap"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    fontSize: 4,
    weight: "semiBold",
    s: {
      mr: 2
    }
  }, cost[0].name)), /*#__PURE__*/React.createElement(Table.Td, {
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4
  }, cost.reduce((prev, curr) => prev + curr.requestCount, 0) ? cost.reduce((prev, curr) => prev + curr.requestCount, 0).toLocaleString() : "-")), /*#__PURE__*/React.createElement(Table.Td, {
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4
  }, cost.reduce((prev, curr) => prev + curr.totalTokenCount, 0) ? helpers.formatTokens(cost.reduce((prev, curr) => prev + curr.totalTokenCount, 0)) : "-")), /*#__PURE__*/React.createElement(Table.Td, {
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4
  }, cost.reduce((prev, curr) => prev + curr.inputTokenCount, 0) ? helpers.formatTokens(cost.reduce((prev, curr) => prev + curr.inputTokenCount, 0)) : "-")), /*#__PURE__*/React.createElement(Table.Td, {
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4
  }, cost.reduce((prev, curr) => prev + curr.outputTokenCount, 0) ? helpers.formatTokens(cost.reduce((prev, curr) => prev + curr.outputTokenCount, 0)) : "-")), /*#__PURE__*/React.createElement(Table.Td, {
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4
  }, cost.reduce((prev, curr) => prev + curr.volume, 0) ? helpers.formatBytes(cost.reduce((prev, curr) => prev + curr.volume, 0)) : "-")), /*#__PURE__*/React.createElement(Table.Td, {
    style: {
      textAlign: "right"
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    weight: "semiBold"
  }, helpers.currency(cost.reduce((prev, curr) => prev + curr.costs, 0))))))) : /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Td, {
    colSpan: columnCount,
    noHover: true,
    center: true
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    display: "block",
    s: {
      my: 5
    }
  }, t("CostExplorerCard.noCostsFound")))))))), /*#__PURE__*/React.createElement(Modal, {
    open: showFilter
  }, /*#__PURE__*/React.createElement(CostExplorerFilterModal, {
    onClose: () => setShowFilter(false)
  })));
};
export default CostExplorerCard;